import classNames from 'classnames';
import EcoImg from 'eco-design/base/eco-img';
import React, { useEffect, useState } from 'react';
import {
    EcoCompareSelectorItemType,
    EcoCompareSelectorType,
    EcoCompareSelectorHeaderItemType,
    EcoCompareSelectorSpecItemType,
    EcoCompareSelectorColorItemType,
    EcoCompareSelectorBtnType,
} from './type';
import EcoSelector from 'eco-design/base/eco-selector';
import EcoTitle from 'eco-design/base/eco-title';
import EcoSpaceSmall from 'eco-design/base/eco-space-small';
import './index.scss';
import { EcoSelectorItemType } from 'eco-design/base/eco-selector/typs';
import EcoLink from 'eco-design/base/eco-link';
import gtm from 'eco-design-reliance/gtm';
import { cloneDeep } from 'lodash';

/**
 * @param name 产品对比选择器
 *
 * @param description 用于展示多个产品的对比信息，如价格、配置等。 3C页面 => Rapid Pro, Rapid Max, Rapid Magnet使用中
 *
 * 第1种,苹果官网的写法,以【列】为第1遍历,给每个参数子项添加最小高度;以实现对齐;(代价是需要需要计算每行中的最小高度并赋值)
 *
 * 第2种,大疆和安克的写法,以【行】为第1遍历,使用flex或table布局实现对齐;(代价是需要将数据分成一行一行的)
 *
 * 此处结合上述两种写法的优点，以【列】遍历（开发时数据按照列装填），并使用table布局实现对齐（利于性能，避免使用js计算高度导致layout重新渲染引起的性能问题）
 *
 *  @param author kalkin
 *  @param date 2025-04-03
 *  @param version 1.0.0
 */
const EcoCompareSelector: React.FC<EcoCompareSelectorType> = ({ className, theme = 'white', title, linkProps, items }) => {
    const prefixCls = `eco-compare-selector`;
    const compCls = classNames(prefixCls, {
        [`${className}`]: className,
        [`${prefixCls}-theme-${theme}`]: theme,
    });
    const [isM, setIsM] = useState<number>(-1); // 0: 移动端; 1: 桌面端;
    const [lists, setLists] = useState<EcoCompareSelectorItemType[]>([]);
    const [flex, setFlex] = useState<EcoCompareSelectorHeaderItemType[]>([]);
    const [table, setTable] = useState<EcoCompareSelectorSpecItemType[][]>([]);
    const [active, setActive] = useState<EcoCompareSelectorColorItemType[]>([]);
    const [maxNum, setMaxNum] = useState<number>(2);

    const transformTableData = (data: EcoCompareSelectorItemType[]) => {
        const rows: EcoCompareSelectorSpecItemType[][] = [];
        const maxSubsLength = Math.max(...data.map((item: EcoCompareSelectorItemType) => item.subs.length));
        for (let i = 0; i < maxSubsLength; i++) {
            const row: EcoCompareSelectorSpecItemType[] = [];
            data?.map((item: EcoCompareSelectorItemType) => row.push(item.subs[i]));
            rows.push(row);
        }
        return rows;
    };

    const transformFlexData = (data: EcoCompareSelectorItemType[]) => {
        return data.map(
            (item: EcoCompareSelectorItemType) =>
                ({
                    items: item?.items,
                    label: item?.label,
                    title: item?.title,
                    desc: item?.desc,
                    price: item?.price,
                    compareAtPrice: item?.compareAtPrice,
                    colors: item?.colors,
                    pc: item?.pc,
                    lHref: item?.lHref,
                    rHref: item?.rHref,
                    btns: item?.btns,
                    subs: item?.subs,
                } as EcoCompareSelectorHeaderItemType),
        );
    };

    const initActive = (data: EcoCompareSelectorItemType[]) => {
        const acts: EcoCompareSelectorColorItemType[] = [];
        data?.map((list: EcoCompareSelectorItemType) => {
            if (list?.colors && list?.colors?.length > 0) {
                acts.push(list?.colors[0] as EcoCompareSelectorColorItemType);
            } else {
                acts.push({ color: '', pc: list?.pc || '', lHref: list?.lHref || '', rHref: list?.rHref || '' } as EcoCompareSelectorColorItemType);
            }
        });
        return acts;
    };

    const handleChangeColor = (k: number, color: EcoCompareSelectorColorItemType) => {
        const acts = cloneDeep(active) as EcoCompareSelectorColorItemType[];
        acts[k] = color;
        setActive(acts);

        gtm.push({
            event: 'uaEvent',
            event_name: 'product_detail_page',
            list_name: title,
            button_name: `${lists?.[k].title}_${color.color}`,
        });
    };

    const handleSelectChange = (seleted: EcoSelectorItemType, k: number) => {
        const newLists = cloneDeep(lists) as EcoCompareSelectorItemType[];
        const newList = items?.find((item: EcoCompareSelectorItemType) => item?.title === seleted.value);
        if (!newList) return;
        newLists[k] = newList as EcoCompareSelectorItemType;
        setLists(newLists);

        gtm.push({
            event: 'uaEvent',
            event_name: 'product_detail_page',
            list_name: title,
            button_name: `Select Box${k + 1}_${seleted.label}`,
        });
    };

    useEffect(() => {
        if (typeof window !== 'undefined') {
            setIsM(window.innerWidth <= 1024 ? 0 : 1);
            setMaxNum(window.innerWidth <= 1024 ? 2 : 4);
        }
    }, []);

    useEffect(() => {
        setLists(items.slice(0, isM === 0 ? 2 : isM === 1 ? 4 : 0));
    }, [isM, items]);

    useEffect(() => {
        setTable(transformTableData(lists));
        setFlex(transformFlexData(lists));
        setActive(initActive(lists));
    }, [lists]);

    return (
        <div className={compCls}>
            {title && <EcoTitle title={title} />}
            {linkProps && <EcoLink {...linkProps} />}
            <EcoSpaceSmall />
            {flex?.some((list: EcoCompareSelectorHeaderItemType) => list?.items?.length > 0) &&
                flex?.some((list: EcoCompareSelectorHeaderItemType) => list?.items?.length > maxNum) && (
                    <table className={`${prefixCls}-table`}>
                        <tr className={`${prefixCls}-table-tr`}>
                            {flex?.map((item: EcoCompareSelectorHeaderItemType, k: number) => {
                                const inUseOptions = flex.map((sub: EcoCompareSelectorHeaderItemType) => sub.title);
                                const restOptions: EcoSelectorItemType[] = [];
                                item?.items?.map((selectItem: EcoSelectorItemType) => {
                                    if (!inUseOptions.includes(String(selectItem.value))) {
                                        restOptions.push(selectItem);
                                    }
                                });
                                // restOptions变化需更新EcoSelector的items, 这里key需绑定restOptions, 否则EcoSelector的items不会更新
                                return (
                                    <td className={`${prefixCls}-table-tr-td ${prefixCls}-table-tr-td-items`} key={`${k}_${JSON.stringify(restOptions)}`}>
                                        {item?.items?.length > 0 && (
                                            <EcoSelector
                                                defaultValue={item?.title}
                                                items={restOptions}
                                                theme={theme}
                                                onClick={() => {
                                                    gtm.push({
                                                        event: 'uaEvent',
                                                        event_name: 'product_detail_page',
                                                        list_name: title,
                                                        button_name: `Select Box${k + 1}`,
                                                    });
                                                }}
                                                onChange={(selected: EcoSelectorItemType) => handleSelectChange(selected, k)}
                                            />
                                        )}
                                    </td>
                                );
                            })}
                        </tr>
                    </table>
                )}
            {(flex?.some((list: EcoCompareSelectorHeaderItemType) => list?.pc) ||
                flex?.some(
                    (list: EcoCompareSelectorHeaderItemType) =>
                        list?.colors && list?.colors?.length > 0 && list?.colors?.some((color: EcoCompareSelectorColorItemType) => color?.pc),
                )) && (
                <table className={`${prefixCls}-table`}>
                    <tr className={`${prefixCls}-table-tr`}>
                        {flex?.map((_: EcoCompareSelectorHeaderItemType, k: number) => (
                            <td className={`${prefixCls}-table-tr-td ${prefixCls}-table-tr-td-img`} key={k}>
                                {active[k]?.pc && <EcoImg pc={active[k]?.pc || ''} key={k} />}
                            </td>
                        ))}
                    </tr>
                </table>
            )}
            {flex?.some((list: EcoCompareSelectorHeaderItemType) => list?.colors) && (
                <table className={`${prefixCls}-table`}>
                    <tr className={`${prefixCls}-table-tr`}>
                        {flex?.map((item: EcoCompareSelectorHeaderItemType, k: number) => (
                            <td className={`${prefixCls}-table-tr-td ${prefixCls}-table-tr-td-color`} key={k}>
                                <ul className={`${prefixCls}-table-tr-td-colors`}>
                                    {item?.colors?.map((color: EcoCompareSelectorColorItemType, m: number) => (
                                        <li
                                            className={`${prefixCls}-table-tr-td-colors-color${
                                                JSON.stringify(active[k]) === JSON.stringify(color) ? ` ${prefixCls}-table-tr-td-colors-color-active` : ''
                                            }`}
                                            style={{ '--background-color': color.color }}
                                            key={`${k}_${color?.color}_${m}`}
                                            onClick={() => handleChangeColor(k, color)}
                                        ></li>
                                    ))}
                                </ul>
                            </td>
                        ))}
                    </tr>
                </table>
            )}
            {flex?.some((list: EcoCompareSelectorHeaderItemType) => list?.label) && (
                <table className={`${prefixCls}-table`}>
                    <tr className={`${prefixCls}-table-tr`}>
                        {flex?.map((item: EcoCompareSelectorHeaderItemType, k: number) => (
                            <td className={`${prefixCls}-table-tr-td ${prefixCls}-table-tr-td-label`} key={k}>
                                {item?.label}
                            </td>
                        ))}
                    </tr>
                </table>
            )}
            {flex?.some((list: EcoCompareSelectorHeaderItemType) => list?.title) && (
                <table className={`${prefixCls}-table`}>
                    <tr className={`${prefixCls}-table-tr`}>
                        {flex?.map((item: EcoCompareSelectorHeaderItemType, k: number) => (
                            <td className={`${prefixCls}-table-tr-td ${prefixCls}-table-tr-td-title`} key={k}>
                                {item?.title}
                            </td>
                        ))}
                    </tr>
                </table>
            )}
            {flex?.some((list: EcoCompareSelectorHeaderItemType) => list?.desc) && (
                <table className={`${prefixCls}-table`}>
                    <tr className={`${prefixCls}-table-tr`}>
                        {flex?.map((item: EcoCompareSelectorHeaderItemType, k: number) => (
                            <td className={`${prefixCls}-table-tr-td ${prefixCls}-table-tr-td-desc`} key={k}>
                                {item?.desc}
                            </td>
                        ))}
                    </tr>
                </table>
            )}
            {(flex?.some((list: EcoCompareSelectorHeaderItemType) => list?.btns && list?.btns?.length > 0) ||
                flex?.some(
                    (list: EcoCompareSelectorHeaderItemType) =>
                        list?.colors &&
                        list?.colors?.length > 0 &&
                        list?.colors?.some((color: EcoCompareSelectorColorItemType) => color?.btns && color?.btns?.length > 0),
                )) && (
                <table className={`${prefixCls}-table`}>
                    <tr className={`${prefixCls}-table-tr`}>
                        {flex?.map((item: EcoCompareSelectorHeaderItemType, k: number) => (
                            <td className={`${prefixCls}-table-tr-td ${prefixCls}-table-tr-td-btn`} key={k}>
                                <div className={`${prefixCls}-table-tr-td-btns`}>
                                    {(active[k]?.btns ? active[k].btns : item?.btns)?.map(
                                        (btn: EcoCompareSelectorBtnType, m: number) =>
                                            (btn?.href || btn?.onClick) && (
                                                <EcoLink
                                                    className={`${btn?.className} ${prefixCls}-table-tr-td-btns-btn ${prefixCls}-table-tr-td-btns-btn-type-${
                                                        btn?.type || 'filled'
                                                    } ${btn.disabled ? 'disabled' : ''}`}
                                                    href={btn?.href}
                                                    dataLayer={btn?.dataLayer}
                                                    onClick={btn?.onClick}
                                                    key={`${k}_${m}_${btn?.href}`}
                                                >
                                                    <span className={`${prefixCls}-table-tr-td-btns-btn-text`}>{btn?.text}</span>
                                                    {btn?.icon && (
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none">
                                                            <path
                                                                d="M6 4.5L10 8.5L6 12.5"
                                                                stroke="currentColor"
                                                                stroke-width="1.5"
                                                                stroke-linecap="round"
                                                                stroke-linejoin="round"
                                                            />
                                                        </svg>
                                                    )}
                                                </EcoLink>
                                            ),
                                    )}
                                </div>
                            </td>
                        ))}
                    </tr>
                </table>
            )}
            {table?.some((row: EcoCompareSelectorSpecItemType[]) => row?.length > 0) && (
                <ul className={`${prefixCls}-lines`}>
                    {flex?.map((_: EcoCompareSelectorHeaderItemType, k: number) => (
                        <li className={`${prefixCls}-lines-line`} key={k}></li>
                    ))}
                </ul>
            )}
            <table className={`${prefixCls}-table`}>
                {table?.map((row: EcoCompareSelectorSpecItemType[], k: number) => (
                    <tr className={`${prefixCls}-table-tr ${prefixCls}-table-tr-spec`} key={k}>
                        {row?.map((column: EcoCompareSelectorSpecItemType, m: number) => (
                            <td className={`${prefixCls}-table-tr-td ${prefixCls}-table-tr-td-spec`} key={`${k}_${m}`}>
                                <div className={`${prefixCls}-table-tr-td-spec-name`}>{column?.title}</div>
                                <div className={`${prefixCls}-table-tr-td-spec-text`}>{column?.desc}</div>
                            </td>
                        ))}
                    </tr>
                ))}
            </table>
        </div>
    );
};

export default EcoCompareSelector;
